import { Alert, AlertProps } from "@op/opds.opux";
import { ReactNode, createContext, useContext, useState } from "react";
import { FormattedMessage } from "react-intl";

export type FlashMessageState = {
  id: string;
  values: Record<string, string>;
  type?: AlertProps["type"];
};

type FlashMessageProviderProps = {
  children: ReactNode;
};

type FlashMessageContext = {
  showMessage: (message: FlashMessageState) => void;
};

const flashMessageCtx = createContext<FlashMessageContext | null>(null);

const { Provider } = flashMessageCtx;

export const FlashMessageProvider = ({
  children,
}: FlashMessageProviderProps) => {
  const [message, setMessage] = useState<FlashMessageState>({
    id: "",
    values: {},
    type: "positive",
  });
  const [show, setShow] = useState(false);

  const showMessage = (msg: FlashMessageState) => {
    setMessage(msg);
    setShow(true);
  };

  const onHide = () => {
    setShow(false);
    setMessage({
      id: "",
      values: {},
      type: "positive",
    });
  };

  return (
    <Provider value={{ showMessage }}>
      {children}
      {show && (
        <Alert
          flash
          type={message.type ?? "positive"}
          onHide={onHide}
          contentType="dynamic"
          className="no-print"
        >
          {message.id && (
            <FormattedMessage id={message.id} values={message.values} />
          )}
        </Alert>
      )}
    </Provider>
  );
};

export const useFlashMessage = () => {
  const context = useContext(flashMessageCtx);
  if (!context) {
    throw Error("FlashMessageContext must be used inside FlashMessageProvider");
  }
  return context.showMessage;
};
